import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { uniqBy, map } from 'lodash';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip as ShadcnTooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@cargomatic/ui';
import {
  retrieveRailsightShipperStatus,
  retrieveContainerStatus,
  updateContainerDetails,
} from 'utils/ContainerTrackAPIUtils';
import moment from 'moment';
import ToastStore from 'stores/ToastStore';
import { humanize, titleize } from 'underscore.string';
import CargomaticCollapsibleCard from 'components/v2/CollapsibleCard';
import Badge from 'components/v2/Badge';
import CircleButton from 'components/v2/buttons/CircleButton';
import RefreshIcon from 'components/v2/icons/RefreshIcon';
import LoadingCircle from 'components/ui/LoadingCircle';
import { withTranslation } from 'react-i18next';
import formatMoney from 'utils/formatters/Money';
import { getOpentrackTrackingInfo } from 'utils/opentrack-utils';
import Alert from 'components/ui/Alert';
import { OPENTRACK_FAILURE_REASONS } from 'constants/Opentrack';
import { RAILSIGHT_STATUS } from 'constants/Railsight';

import DescriptionListItem from '../../v2/DescriptionListItem';

class PortIntegration extends Component {
  constructor(props) {
    super(props);
    this.state = this.getInitialState();
    this.refreshFunction = null;
  }

  getInitialState() {
    const { isShipment } = this.props;
    const containers = this.getContainers(this.props);
    const masterBol = this.getMasterBol(this.props);
    return {
      updates: [],
      railSightActive: false,
      isShipment,
      containers,
      isRefreshing: false,
      masterBol,
    };
  }

  parseContainersFromShipments(stops = []) {
    if (stops.length === 0) {
      return [];
    }
    return uniqBy(
      stops.reduce((equipmentList, stop) => {
        const stopContainers = (stop.equipments || []).filter(
          (eq) => eq._equipmentType === 'container',
        );
        return equipmentList.concat(stopContainers);
      }, []),
      (eq) => eq._id,
    );
  }

  parseContainersFromOrders(containers = []) {
    if (containers.length === 0) {
      return [];
    }
    let uniqueContainers = uniqBy(containers, '_id');
    uniqueContainers = uniqueContainers.filter(
      (eq) => eq._equipmentType === 'container',
    );
    return uniqueContainers;
  }

  getContainers(props) {
    const { data, isShipment } = props;
    let stops;
    if (isShipment) {
      stops = data.stops;
    } else {
      return this.parseContainersFromOrders(data.equipments);
    }
    return this.parseContainersFromShipments(stops);
  }

  getMasterBol(props) {
    const { data, isShipment } = props;
    if (!isShipment || !data?.reference_numbers) {
      return null;
    }

    const { reference_numbers } = data;
    return reference_numbers.find((r) => r.name === 'master_bol')?.optimized;
  }

  setRefreshFunction = () => {
    this.refreshFunction = setInterval(async () => {
      this.getContainerData();
    }, 20000);
  };

  componentDidMount() {
    this.setRefreshFunction();
    this.getContainerData();
    this.getRailsightStatus();
  }

  componentDidUpdate(prevProps) {
    const prevId = prevProps.data?._id;
    const prevIsShipment = prevProps.isShipment;

    const { isShipment, data } = this.props;
    const id = data?._id;

    if (prevIsShipment !== isShipment || prevId !== id) {
      clearInterval(this.refreshFunction);
      const containers = this.getContainers(this.props);
      this.setState(
        {
          containers,
          isShipment,
        },
        () => {
          this.setRefreshFunction();
          this.getRailsightStatus();
          this.getContainerData();
        },
      );
    }
  }

  componentWillUnmount() {
    clearInterval(this.refreshFunction);
  }

  getStatusColor(status) {
    if (!status) {
      return 'gray';
    }
    switch (status) {
      case 'VESSEL_ARRIVED':
      case 'DISCHARGED':
        return 'orange';
      case 'LOADED':
      case 'FULL_OUTGATED':
      case 'FULL_INGATED':
      case 'VESSEL_DEPARTED':
        return 'blue';
      case 'AVAILABLE':
      case 'HOLD':
        return 'red';
      default:
        return 'gray';
    }
  }

  getTrackingStoppedFailureText(failureReason, t) {
    switch (failureReason) {
      case OPENTRACK_FAILURE_REASONS.UNTRACKABLE_CONTAINER:
        return t('UNTRACKABLE_CONTAINER');
      case OPENTRACK_FAILURE_REASONS.UNTRACKABLE_BOL:
        return t('UNTRACKABLE_BOL');
      case OPENTRACK_FAILURE_REASONS.NO_MORE_REPORTS:
        return t('NO_MORE_REPORTS');
      case OPENTRACK_FAILURE_REASONS.OUTDATED_SHIPMENT:
        return t('OUTDATED_SHIPMENT');
      default:
        return failureReason;
    }
  }

  renderHolds = (holds) => {
    const { t } = this.props;
    return holds.map((hold, i) => {
      return (
        <ul key={i}>
          <li>
            {t('REPORTED_WITH_VALUE', {
              value: hold.reported_at
                ? moment(hold.reported_at).format('L')
                : t('NOT_AVAILABLE'),
            })}
            <br />
            {t('TYPE_WITH_VALUE', {
              value: hold.type ? hold.type : t('NOT_AVAILABLE'),
            })}
            <br />
            {t('RESOLVED_WITH_VALUE', {
              value: hold.resolved_at
                ? moment(hold.resolved_at).format('L')
                : t('NOT_AVAILABLE'),
            })}
          </li>
        </ul>
      );
    });
  };

  getRailsightStatus = async () => {
    const { data: shipment, isShipment, t } = this.props;
    if (isShipment) {
      try {
        const railSightActive = await retrieveRailsightShipperStatus(
          shipment.shipper?.id || shipment.shipper,
        );
        this.setState({ railSightActive });
      } catch (err) {
        this.showNotification(t('RAILSIGHT_STATUS_FAILED'), 'fail');
      }
    }
  };

  getContainerData = async () => {
    const { containers, masterBol } = this.state;

    await this.queryContainerData(containers, masterBol);
  };

  renderTooltipContent = (item) => {
    const { t } = this.props;
    return (
      <div className="text-left space-y-0.5 text-xs max-w-xs">
        <div>
          {t('ETA_WITH_VALUE', {
            value: item?.eta_at_terminal
              ? moment(item.eta_at_terminal).format('L')
              : t('NOT_AVAILABLE'),
          })}
        </div>
        <div>
          {t('ARRIVAL_WITH_VALUE', {
            value: item?.vessel_arrived_date
              ? moment(item.vessel_arrived_date).format('L')
              : t('NOT_AVAILABLE'),
          })}
        </div>
        <div>
          {t('DISCHARGED_WITH_VALUE', {
            value: item?.discharged_date
              ? moment(item.discharged_date).format('L')
              : t('NOT_AVAILABLE'),
          })}
        </div>
        <div>
          {t('LFD_WITH_VALUE', {
            value: item?.last_free_day
              ? moment(item.last_free_day).format('L')
              : t('NOT_AVAILABLE'),
          })}
        </div>
        <div>
          {t('OUTGATED_WITH_VALUE', {
            value: item?.outgated_date
              ? moment(item.outgated_date).format('L')
              : t('NOT_AVAILABLE'),
          })}
        </div>
        <div>
          {t('EMPTY_WITH_VALUE', {
            value: item?.empty_returned_date
              ? moment(item.empty_returned_date).format('L')
              : t('NOT_AVAILABLE'),
          })}
        </div>
        {item.holds && item.holds.length > 0 && (
          <div>
            {titleize(t('HOLD', { count: 2 }))}:{this.renderHolds(item.holds)}
          </div>
        )}
      </div>
    );
  };

  // renderTooltip removed — replaced by renderTooltipContent above

  showNotification(title, level) {
    ToastStore.getNotifications().push({
      title,
      body: '',
      level,
    });
    ToastStore.emitChange();
  }

  async refreshContainer(container, masterBol) {
    const { t } = this.props;
    try {
      this.setState({ isRefreshing: true });
      const response = await updateContainerDetails(container, masterBol);
      if (response && response.success) {
        this.updateContainer(response.data);
        this.showNotification(t('CONTAINER_REFRESHED_SUCCESSFULLY'), 'success');
        this.setState({ isRefreshing: false });
      }
    } catch (err) {
      this.showNotification(t('CONTAINER_REFRESHED_FAILED'), 'fail');
      this.setState({ isRefreshing: false });
    }
  }

  queryContainerData = async (containers, masterBol) => {
    if (containers && containers.length) {
      try {
        const response = await retrieveContainerStatus(
          map(containers, 'equipment_id'),
          masterBol,
        );
        if (
          response &&
          response.success === true &&
          response.results &&
          response.results.length
        ) {
          this.setState({
            updates: response.results,
          });
        }
      } catch (err) {
        console.log('Failed to retrieve server status', err);
      }
    }
  };

  updateContainer = (data) => {
    const { updates: currentData } = this.state;
    const containerToUpdateIndex = currentData.findIndex(
      (container) => container._id === data._id,
    );
    currentData[containerToUpdateIndex] = data;
    this.setState({ updates: currentData });
  };

  renderRefresh = (item, update) => {
    const { isRefreshing } = this.state;
    if (isRefreshing) {
      return <LoadingCircle />;
    }
    const difference = moment().diff(moment(item.last_updated), 'minutes');
    const canRefresh = difference > 5;

    return (
      <div className="flex ml-4">
        <CircleButton
          color="blue"
          disabled={!canRefresh}
          onClick={() => {
            this.refreshContainer(item.container_id, update.master_bill_number);
          }}
        >
          <RefreshIcon size="medium" />
        </CircleButton>
      </div>
    );
  };

  renderTrackingStopped(container_id, failureReason, t) {
    const failureMessage = this.getTrackingStoppedFailureText(failureReason, t);
    return (
      <Alert className="m-4" type="warning" title={t('STOPPED_TRACKING')}>
        {failureMessage}
      </Alert>
    );
  }

  renderRow(updates) {
    const update = updates[0];
    const { t } = this.props;
    return updates
      .filter((x) => !x.is_tracking_stopped)
      .map((item) => (
        <TableRow key={item.container_id}>
          <TableCell>{item.container_id}</TableCell>
          <TableCell>
            <TooltipProvider>
              <ShadcnTooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Badge color={this.getStatusColor(item.status)}>
                      {humanize(item.status)}
                    </Badge>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  {this.renderTooltipContent(item)}
                </TooltipContent>
              </ShadcnTooltip>
            </TooltipProvider>
          </TableCell>
          <TableCell>
            {moment(item.last_updated).format('MMMM Do YYYY, h:mm:ss a')}
          </TableCell>
          <TableCell>{this.renderRefresh(item, update)}</TableCell>
        </TableRow>
      ));
  }

  getHeaderRailsightStatus(update, t) {
    return {
      status: t(RAILSIGHT_STATUS.RAILSIGHT_ENABLED),
      badgeColor: 'green',
    };
  }

  getHeaderStatus(opentrackMeta, update) {
    return getOpentrackTrackingInfo(opentrackMeta?.status, update);
  }

  renderHeader = (updates, opentrackMeta, t) => {
    const update = updates.length ? updates[0] : null;
    const { isShipment } = this.props;
    const { railSightActive } = this.state;
    const { localizationSlug, badgeColor } = this.getHeaderStatus(
      opentrackMeta,
      update,
    );
    const railsightStatus = this.getHeaderRailsightStatus(update, t);

    return (
      <div className="flex">
        <h3 className="my-0 mr-4">{t('PORT_INTEGRATIONS')}</h3>
        <Badge className="mt-1" color={badgeColor}>
          {t(localizationSlug)}
        </Badge>
        {isShipment && railSightActive && (
          <Badge className="mt-1" color={railsightStatus.badgeColor}>
            {railsightStatus.status}
          </Badge>
        )}
      </div>
    );
  };

  renderBody(updates, opentrackMeta, t) {
    if (updates.length === 0) {
      if (opentrackMeta?.failure_reason) {
        return this.renderTrackingStopped(
          opentrackMeta.container_id,
          opentrackMeta.failure_reason,
          t,
        );
      }
      return (
        updates.length === 0 && (
          <p className="px-4 py-4 sm:px-6 sm:py-6">{t('NO_UPDATES')}</p>
        )
      );
    }
    return (
      <>
        {updates
          .filter((x) => x.is_tracking_stopped)
          .map((x) =>
            this.renderTrackingStopped(
              x.container_id,
              x.tracking_stopped_reason,
              t,
            ),
          )}
        {updates.filter((x) => !x.is_tracking_stopped).length > 0 && (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{titleize(t('NUMBER'))}</TableHead>
                  <TableHead>{titleize(t('STATE'))}</TableHead>
                  <TableHead>{titleize(t('UPDATED'))}</TableHead>
                  <TableHead>{titleize(t('REFRESH'))}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>{this.renderRow(updates)}</TableBody>
            </Table>
          </div>
        )}
      </>
    );
  }

  render() {
    const {
      t,
      isShipment,
      data: { opentrack_metadata: opentrackMeta },
    } = this.props;
    const { updates } = this.state;
    return (
      <CargomaticCollapsibleCard
        id="port-integrations"
        header={() => this.renderHeader(updates, opentrackMeta, t)}
      >
        {this.renderBody(updates, opentrackMeta, t)}
        {isShipment && this.props.data?.demurrage_incurred && (
          <>
            <hr className="my-4" />
            <DescriptionListItem label={t('ESTIMATED_DEMURRAGE_OWED')}>
              <div className="text-lg font-medium text-gray-800">
                {formatMoney(
                  this.props.data.demurrage_incurred || 0,
                  this.props.data.currency || 'USD',
                )}
              </div>
            </DescriptionListItem>
          </>
        )}
      </CargomaticCollapsibleCard>
    );
  }
}

PortIntegration.propTypes = {
  isShipment: PropTypes.bool.isRequired,
  data: PropTypes.object.isRequired,
};

export default withTranslation()(PortIntegration);
